import { useCallback } from 'react';

import type {
  Devices,
  DevicesMetadata,
  MinimalDevice,
  QueryRequest,
} from '@rtkwlf/asset-data-service';
import type { ResponseError } from '@rtkwlf/capability';
import type {
  InfiniteData,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';

import { useOrganizationContext } from '@packages/legacy/organization-context';
import { useFeatureFlag } from '@packages/platform/feature-flags/use-feature-flag';

import { useAssetDataServiceApi } from '../use-asset-data-service-api';

import { mockDevices } from './mocks/assets.mock';

type DeviceWithPageParam = Devices & {
  pageParam: QueryRequest;
};

export type TransformedDevicesResponse = {
  devices: MinimalDevice[] | undefined;
  metadata: DevicesMetadata;
  pageParam: QueryRequest;
}[];

type UseGetAllAssetsOptions = Partial<
  Omit<
    UseInfiniteQueryOptions<
      DeviceWithPageParam,
      ResponseError,
      TransformedDevicesResponse,
      DeviceWithPageParam,
      ReturnType<typeof getAllAssetsQueryKey>,
      QueryRequest
    >,
    'queryKey' | 'queryFn'
  >
>;

export type UseGetAllAssetsParams = {
  options?: UseGetAllAssetsOptions;
  queryParams?: QueryRequest;
};

export const DEFAULT_PAGE_QUERY_PARAM: QueryRequest = {
  limit: 2000,
  offset: 0,
  filter: {
    operation: {
      operator: 'isnull',
      arguments: [
        {
          field: {
            name: 'deleted_time',
          },
        },
      ],
    },
  },
  sort: [{ field: { name: 'last_seen_time' }, order: 'desc' }],
};

const selectAssets = (
  data: InfiniteData<DeviceWithPageParam, QueryRequest>,
) => {
  return data?.pages?.map((page) => {
    const { devices, ...rest } = page;
    return {
      ...rest,
      devices: devices ? Array.from(devices) : undefined,
    };
  });
};

export const getAllAssetsQueryKey = (organizationId: string) => {
  return [organizationId, 'ads', 'get-all-assets'] as const;
};

const mockQueryFn = ({
  queryRequest,
}: {
  organizationId?: string;
  queryRequest: QueryRequest;
}) => {
  const { offset, limit } = queryRequest;
  const data: Devices = {
    devices: new Set(mockDevices.slice(offset, offset + limit)),
    metadata: {
      total: mockDevices.length,
    },
  };

  return new Promise<Devices>((resolve) => {
    setTimeout(() => resolve(data), 1000);
  });
};

const getNextPageParam = (prevPage: DeviceWithPageParam) => {
  const { offset, limit } = prevPage.pageParam;
  const nextOffset = offset + limit;

  if (nextOffset >= prevPage.metadata.total) {
    return undefined;
  }
  return { ...prevPage.pageParam, offset: nextOffset };
};

/**
 * This hook uses infinite query to progressively load large sets of asset data.
 * It handles pagination automatically and provides transformation utilities.
 *
 * @param params - Configuration parameters for the query
 * @param params.options - Optional query options to customize the behavior of the infinite query
 * @param params.queryParams - Optional custom query parameters supported by the API
 *
 * @returns An infinite query result containing paginated device data
 * @remarks
 * Order of execution:
 * queryFn is called with DEFAULT_PAGE_QUERY_PARAM or queryParams if provided.
 * The response includes both data and the pageParams used.
 * This response is passed to getNextPageParam to compute the next page param.
 * If a next page param is returned, queryFn is called again with it.
 * The cycle repeats until getNextPageParam returns undefined.
 */

export const useGetAllAssets = ({
  options,
  queryParams,
}: UseGetAllAssetsParams = {}) => {
  const api = useAssetDataServiceApi();
  const { selectedOrganization } = useOrganizationContext();
  const organizationId = selectedOrganization.id;

  const [fullMockedFF] = useFeatureFlag('ADS_FULL_MOCKED');

  const getAllAssets = fullMockedFF ? mockQueryFn : api.queryDevices.bind(api);

  const queryFn = useCallback(
    async ({ pageParam }: { pageParam: QueryRequest }) => {
      const response = await getAllAssets({
        organizationId,
        queryRequest: pageParam,
      });
      return { ...response, pageParam };
    },
    [getAllAssets, organizationId],
  );

  return useInfiniteQuery({
    queryKey: getAllAssetsQueryKey(organizationId),
    initialPageParam: queryParams ?? DEFAULT_PAGE_QUERY_PARAM,
    queryFn,
    retry: 0,
    // Handle error in the isError state, before throwing directly to the error boundary.
    throwOnError: false,
    select: selectAssets,
    getNextPageParam,
    ...options,
  });
};
